// frontend/src/contexts/TrackingContext.jsx
// Context for managing tracked items - prevents duplicate API calls
// Fetches tracked items once and shares across all DealCard components

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { getTrackedItems } from "../services/tracking";
import { useAuth } from "./AuthContext";

const TrackingContext = createContext(null);

export const TrackingProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [trackedItems, setTrackedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchPromiseRef = useRef(null); // Prevent concurrent fetches

  /**
   * Fetch tracked items from API
   * Uses a promise ref to prevent concurrent calls
   */
  const fetchTrackedItems = useCallback(async () => {
    // If already fetching, return the existing promise
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }

    if (!isAuthenticated) {
      setTrackedItems([]);
      setError(null);
      return [];
    }

    setIsLoading(true);
    setError(null);

    // Create a promise and store it in ref
    const promise = (async () => {
      try {
        const items = await getTrackedItems();
        setTrackedItems(items);
        setError(null);
        return items;
      } catch (err) {
        // Silently handle authentication errors (401, session expired, etc.)
        const isAuthError = 
          err.response?.status === 401 ||
          err.message?.includes("401") ||
          err.message?.includes("Unauthorized") ||
          err.message?.includes("session has expired") ||
          err.message?.includes("session expired");
        
        if (!isAuthError) {
          console.error("TrackingContext: Error fetching tracked items:", err);
        }
        // Don't set error for auth failures - they're expected when session expires
        if (!isAuthError) {
          setError(err.message);
        }
        setTrackedItems([]);
        return [];
      } finally {
        setIsLoading(false);
        fetchPromiseRef.current = null; // Clear the ref when done
      }
    })();

    fetchPromiseRef.current = promise;
    return promise;
  }, [isAuthenticated]);

  /**
   * Refresh tracked items (force new fetch)
   */
  const refreshTrackedItems = useCallback(async () => {
    fetchPromiseRef.current = null; // Clear any existing promise
    return fetchTrackedItems();
  }, [fetchTrackedItems]);

  /**
   * Check if a specific deal/item is tracked
   */
  const isItemTracked = useCallback(
    (dealId) => {
      return trackedItems.some(
        (item) => 
          item.item_id === dealId || 
          item.deal_id === dealId ||
          (item.item && item.item.id === dealId) ||
          (item.item && item.item.item_id === dealId)
      );
    },
    [trackedItems]
  );

  /**
   * Get tracking info for a specific deal/item
   */
  const getTrackingInfo = useCallback(
    (dealId) => {
      return trackedItems.find(
        (item) => 
          item.item_id === dealId || 
          item.deal_id === dealId ||
          (item.item && item.item.id === dealId) ||
          (item.item && item.item.item_id === dealId)
      ) || null;
    },
    [trackedItems]
  );

  // Fetch tracked items when authenticated status changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchTrackedItems();
    } else {
      setTrackedItems([]);
      setError(null);
      fetchPromiseRef.current = null;
    }
  }, [isAuthenticated, fetchTrackedItems]);

  const value = {
    trackedItems, // Expose trackedItems so components can depend on it directly
    isLoading,
    error,
    fetchTrackedItems,
    refreshTrackedItems,
    isItemTracked,
    getTrackingInfo,
  };

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracking = () => {
  const context = useContext(TrackingContext);
  if (context === null) {
    // Return safe defaults if used outside provider
    return {
      trackedItems: [],
      isLoading: false,
      error: null,
      fetchTrackedItems: async () => [],
      refreshTrackedItems: async () => [],
      isItemTracked: () => false,
      getTrackingInfo: () => null,
    };
  }
  return context;
};

export { TrackingContext };

